// Links into a dealer's page (#419): the dealer opt-in email opens
//   /home/dealerdetails?dealerId=&tenantId=&tab=&filter=
// on the right tab, filter and tenant. jbwdealerpendingagreementsclaimsnotification builds them.
//
// Everything here is read off a URL a recipient can edit, so nothing is trusted: a tab or filter
// the screens do not offer is dropped, ids must be digits, and the path to return to after sign-in
// must stay inside the portal (no open redirect through the sign-in page).

// The option values AgreementsGrid and ClaimsGrid actually offer.
const FILTERS = {
  agreements: ["Pending", "Saved", "Paid", "All", "Potential"],
  claims: ["Potential Claims", "Processing", "Missing Documents", "Denied", "Revive", "Approved and Paid", "All"],
};

// DealerDetails' tab keys use the admin numbering (0 INVOICES, 1 AGREEMENTS, 2 CLAIMS);
// AgreementsClaimsTabs shifts them for a non-admin, who has no INVOICES tab.
const TAB_KEYS = { agreements: 1, claims: 2 };

const digits = (value) => (/^\d+$/.test(value ?? "") ? value : undefined);

export const parseDealerLink = (search) => {
  const params = new URLSearchParams(search || "");
  const tab = Object.prototype.hasOwnProperty.call(FILTERS, params.get("tab")) ? params.get("tab") : undefined;
  const filter = tab && FILTERS[tab].includes(params.get("filter")) ? params.get("filter") : undefined;
  return {
    dealerId: digits(params.get("dealerId")),
    tenantId: digits(params.get("tenantId")),
    tab,
    tabKey: tab ? TAB_KEYS[tab] : undefined,
    filter,
  };
};

// What DealerDetails does with a link once it knows who is signed in -- one decision, kept pure so
// it can be tested (the dealer-user branches cannot be driven locally, where sign-in is bypassed):
//   "none"    no link on the URL
//   "wait"    a link, but the user's group has not loaded yet
//   "apply"   the link is for the dealer already open (or names none): open its tab, filter, tenant
//   "switch"  an admin following a link to another dealer: switch to that dealer, then apply
//   "drop"    a dealer user's link for a dealer they are not signed in as: ignore all of it
export const linkAction = ({ link, group, currentDealerId }) => {
  if (!(link.dealerId || link.tenantId || link.tab)) return "none";
  if (!group) return "wait";
  if (!link.dealerId || String(currentDealerId) === link.dealerId) return "apply";
  return group === "jbw-admin" ? "switch" : "drop";
};

// The starting filter for a grid, from navigation state: what DealerSummaryAdminTool sets, or a
// link DealerDetails has turned into state. A link's filter carries the tab it is for
// (initialFilterTab), so the other tab's grid ignores it; Dealer Summary's carries none and
// applies as it always has. Otherwise the grid's own default.
export const initialFilter = (location, tab, fallback) => {
  const state = location?.state;
  if (!state?.initialFilter) return fallback;
  return !state.initialFilterTab || state.initialFilterTab === tab ? state.initialFilter : fallback;
};

// Where to send a user after sign-in: the page they were sent away from, if it is inside the
// portal, else the default.
export const returnPath = (from, fallback) => {
  const path = typeof from === "string" ? from : from ? `${from.pathname ?? ""}${from.search ?? ""}` : "";
  return path.startsWith("/home/") && !path.startsWith("//") ? path : fallback;
};

// The routes a dealer user's menu offers (components/Sidebar.jsx, dealerMenuItem). A dealer is
// returned to one of these after sign-in; anything else -- an admin screen, say -- starts them
// on their dealer page instead.
const DEALER_HOME = "/home/dealerdetails";
const DEALER_PATHS = [DEALER_HOME, "/home/todohome", "/home/agreementssynchome", "/home/transactionlog"];

// Where sign-in sends a user, and -- for a dealer user registered to several dealers -- which of
// their registrations to sign them in as. One decision, kept pure so it can be tested; SignIn's
// handleUserNavigation only acts on it.
//
//   group          the Cognito group ("jbw-dealer" for dealer users)
//   from           RouteGuard's state.from: where the user was going before sign-in
//   registrations  checkDealerOnboard's rows for this user, each with a dealerId
//
// A link names its dealer (?dealerId=); a dealer user signs in as that registration when they hold
// it, so an email about their second dealer opens that dealer and not their first.
export const postSignInPath = ({ group, from, registrations = [] }) => {
  if (group !== "jbw-dealer") return { path: returnPath(from, "/home/overview") };

  const target = returnPath(from, DEALER_HOME);
  const pathname = target.split("?")[0];
  const path = DEALER_PATHS.includes(pathname) ? target : DEALER_HOME;

  const wanted = parseDealerLink(target.includes("?") ? target.slice(target.indexOf("?")) : "").dealerId;
  const registration =
    registrations.find((r) => String(r.dealerId) === wanted) || registrations[0];
  return { path, dealerId: registration?.dealerId };
};
